/**
 * ProviderSelector - Minimal single-word toggle for AI provider selection
 *
 * Shows current provider name, click to switch
 */

import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { ProviderType } from '../../services/messageService';

interface ProviderSelectorProps {
  value: ProviderType;
  onChange: (provider: ProviderType) => void;
  disabled?: boolean;
  className?: string;
}

const PROVIDER_CONFIG = {
  gemini: {
    name: 'Gemini',
    colors: 'text-primary hover:text-accent-foreground',
    bg: 'hover:bg-primary/10',
  },
  claude: {
    name: 'Claude',
    colors: 'text-orange-400 hover:text-orange-300',
    bg: 'hover:bg-orange-500/10',
  },
} as const;

/**
 * Minimal provider toggle - shows current provider, click to switch
 */
export function ProviderSelector({ value, onChange, disabled = false, className }: ProviderSelectorProps) {
  const toggle = () => {
    if (disabled) return;
    onChange(value === 'gemini' ? 'claude' : 'gemini');
  };

  const config = PROVIDER_CONFIG[value];

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled}
      className={cn(
        "flex items-center gap-0.5 px-2 py-1 rounded-lg transition-all duration-150",
        config.colors,
        config.bg,
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
      title={`Using ${config.name} • Click to switch`}
      aria-label={`AI Provider: ${config.name}. Click to switch.`}
    >
      <span className="text-xs font-medium">{config.name}</span>
      <ChevronDown className="h-3 w-3 opacity-60" />
    </button>
  );
}

export default ProviderSelector;
